import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';

const RegistroPage = ({ setCurrentPage }) => {
  const [form, setForm] = useState({
    username: '',
    nombres: '',
    apellidos: '',
    correo: '',
    contraseña: '',
    confirmarContraseña: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({ username: '', nombres: '', apellidos: '', correo: '', contraseña: '', confirmarContraseña: '', verification: '' });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Estados para verificación de correo
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
    // Si cambia el correo, resetear la verificación
    if (name === 'correo') {
      setEmailVerified(false);
      setCodeSent(false);
      setVerificationCode('');
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Función para enviar código de verificación
  const sendVerificationCode = async () => {
    setErrors(prev => ({ ...prev, verification: '' }));
    if (!form.correo) {
      setErrors(prev => ({ ...prev, correo: 'Ingresa un correo primero' }));
      return;
    }

    // Validar formato de correo básico
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.correo)) {
      setErrors(prev => ({ ...prev, correo: 'Formato de correo inválido' }));
      return;
    }

    try {
      setSendingCode(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

      const response = await fetch(`${API_URL}/auth/send-verification-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.correo })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar código');
      }

      setCodeSent(true);
    } catch (error) {
      setErrors(prev => ({ ...prev, verification: error.message }));
    } finally {
      setSendingCode(false);
    }
  };

  // Función para verificar el código
  const verifyCode = async () => {
    setErrors(prev => ({ ...prev, verification: '' }));
    if (!verificationCode || verificationCode.length !== 4) {
      setErrors(prev => ({ ...prev, verification: 'El código debe tener 4 dígitos' }));
      return;
    }

    try {
      setVerifyingCode(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

      const response = await fetch(`${API_URL}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.correo, code: verificationCode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Código inválido o expirado');
      }

      setEmailVerified(true);
    } catch (error) {
      setErrors(prev => ({ ...prev, verification: error.message }));
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError('');
    const newErrors = { username: '', nombres: '', apellidos: '', correo: '', contraseña: '', confirmarContraseña: '', verification: '' };

    if (!form.username.trim()) newErrors.username = 'El usuario es obligatorio';
    else if (form.username.length < 4) newErrors.username = 'El usuario debe tener al menos 4 caracteres';
    if (!form.nombres.trim()) newErrors.nombres = 'Los nombres son obligatorios';
    if (!form.apellidos.trim()) newErrors.apellidos = 'Los apellidos son obligatorios';
    if (!form.correo.trim()) newErrors.correo = 'El correo es obligatorio';
    if (!form.contraseña.trim()) newErrors.contraseña = 'La contraseña es obligatoria';
    else if (form.contraseña.length < 6) newErrors.contraseña = 'La contraseña debe tener al menos 6 caracteres';
    if (!form.confirmarContraseña.trim()) newErrors.confirmarContraseña = 'Confirma tu contraseña';
    else if (form.contraseña !== form.confirmarContraseña) newErrors.confirmarContraseña = 'Las contraseñas no coinciden';

    // Validación final de correo verificado
    if (!emailVerified) newErrors.verification = 'Debes verificar tu correo electrónico antes de continuar';

    setErrors(newErrors);
    // Verificar si hay errores (excluyendo campos vacíos si es que los hubiera por defecto, pero aquí inicializamos con string vacío)
    if (Object.values(newErrors).some(e => e)) {
      return;
    }

    try {
      setLoading(true);
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';
      const payload = {
        username: form.username,
        email: form.correo,
        password: form.contraseña,
        nombres: form.nombres,
        apellidos: form.apellidos,
      };
      const resp = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({}));
        throw new Error(errBody.message || `Error de registro (${resp.status})`);
      }
      // éxito: ir a página de confirmación
      setCurrentPage && setCurrentPage('cuenta-creada');
    } catch (err) {
      setApiError(err.message || 'Error inesperado');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex justify-center items-center p-4">
      {/* Línea azul decorativa arriba */}
      <div className="absolute top-0 left-0 w-full h-2 bg-[rgb(0,51,153)]"></div>

      {/* Contenedor principal más ancho */}
      <div className="bg-white p-8 shadow-xl rounded-2xl w-full max-w-lg border border-gray-100 relative mt-2">
        {/* Logo y texto en línea */}
        <div className="flex items-center justify-center mb-8 gap-6">
          {/* Logo */}
          <div className="w-24 h-24 bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg transform -rotate-12 hover:rotate-0 transition-transform duration-300">
            <span className="text-white font-bold text-3xl transform rotate-12">eSic</span>
          </div>

          {/* Textos al lado del logo - Alineados a la derecha */}
          <div className="text-right">
            <h1 className="text-[rgb(0,51,153)] font-bold text-2xl mb-2">
              Sistema Académico
            </h1>
            <p className="text-gray-600 text-base leading-relaxed">
              Escuela Boliviana<br />Intercultural de Danza
            </p>
          </div>
        </div>

        <h2 className="text-[rgb(0,51,153)] font-bold text-center mb-8 text-xl">Crear Cuenta</h2>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Campo Usuario */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Usuario *</label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              className={`w-full border rounded-full px-4 py-3 text-base outline-none transition-all duration-200 ${errors.username ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-[rgb(0,51,153)] focus:border-transparent'}`}
              placeholder="Ej: juan.perez (mín. 4 caracteres)"
            />
            {errors.username && (
              <p className="mt-1 text-sm text-red-600">{errors.username}</p>
            )}
          </div>

          {/* Nombres y Apellidos */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">Nombres *</label>
              <input
                type="text"
                name="nombres"
                value={form.nombres}
                onChange={handleChange}
                className={`w-full border rounded-full px-4 py-3 text-base outline-none transition-all duration-200 ${errors.nombres ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-[rgb(0,51,153)] focus:border-transparent'}`}
                placeholder="Ingresa tus nombres"
              />
              {errors.nombres && (
                <p className="mt-1 text-sm text-red-600">{errors.nombres}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">Apellidos *</label>
              <input
                type="text"
                name="apellidos"
                value={form.apellidos}
                onChange={handleChange}
                className={`w-full border rounded-full px-4 py-3 text-base outline-none transition-all duration-200 ${errors.apellidos ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-[rgb(0,51,153)] focus:border-transparent'}`}
                placeholder="Ingresa tus apellidos"
              />
              {errors.apellidos && (
                <p className="mt-1 text-sm text-red-600">{errors.apellidos}</p>
              )}
            </div>
          </div>

          {/* Correo electrónico */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Correo Electrónico *</label>
            <input
              type="email"
              name="correo"
              value={form.correo}
              onChange={handleChange}
              onInvalid={(e) => e.target.setCustomValidity('Ingresa un correo electrónico válido')}
              onInput={(e) => e.target.setCustomValidity('')}
              className={`w-full border rounded-full px-4 py-3 text-base outline-none transition-all duration-200 ${errors.correo ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-[rgb(0,51,153)] focus:border-transparent'}`}
              placeholder="ejemplo@correo.com"
            />
            {errors.correo && (
              <p className="mt-1 text-sm text-red-600">{errors.correo}</p>
            )}
          </div>
          {/* Contraseña y Confirmar */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">Contraseña *</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="contraseña"
                  value={form.contraseña}
                  onChange={handleChange}
                  className={`w-full border rounded-full px-4 py-3 pr-12 text-base outline-none transition-all duration-200 ${errors.contraseña ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-[rgb(0,51,153)] focus:border-transparent'}`}
                  placeholder="Mín. 6 caracteres"
                />
                <button
                  type="button"
                  onClick={toggleShowPassword}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors duration-200"
                  aria-label="Mostrar/Ocultar contraseña"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.contraseña && (
                <p className="mt-1 text-sm text-red-600">{errors.contraseña}</p>
              )}
            </div>
            <div className="flex-1">
              <label className="block text-gray-700 text-sm font-medium mb-2">Confirmar *</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmarContraseña"
                  value={form.confirmarContraseña}
                  onChange={handleChange}
                  className={`w-full border rounded-full px-4 py-3 pr-12 text-base outline-none transition-all duration-200 ${errors.confirmarContraseña ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-2 focus:ring-[rgb(0,51,153)] focus:border-transparent'}`}
                  placeholder="Repite clave"
                />
                <button
                  type="button"
                  onClick={toggleShowConfirmPassword}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors duration-200"
                  aria-label="Mostrar/Ocultar contraseña"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmarContraseña && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmarContraseña}</p>
              )}
            </div>
          </div>

          {/* Sección de verificación de email - AL FINAL del formulario */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
            <h4 className="text-md font-semibold text-blue-800 mb-3">
              Verificación de Correo Electrónico
            </h4>

            {emailVerified ? (
              <div className="flex items-center gap-2 text-green-600">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">¡Correo verificado correctamente!</span>
              </div>
            ) : (
              <>
                {!codeSent ? (
                  <button
                    type="button"
                    onClick={sendVerificationCode}
                    disabled={sendingCode || !form.correo}
                    className={`w-full py-3 rounded-lg font-semibold transition ${sendingCode || !form.correo
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-900 text-white hover:bg-blue-800'
                      }`}
                  >
                    {sendingCode ? 'Enviando...' : 'Verificar Correo'}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Ingresa el código de 4 dígitos enviado a <strong>{form.correo}</strong>
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        maxLength={4}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                        placeholder="0000"
                        className="flex-1 px-4 py-3 border-2 rounded-lg text-center text-2xl font-bold tracking-widest focus:outline-none focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={verifyCode}
                        disabled={verifyingCode || verificationCode.length !== 4}
                        className={`px-6 py-3 rounded-lg font-semibold transition ${verifyingCode || verificationCode.length !== 4
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                      >
                        {verifyingCode ? '...' : 'Verificar'}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCodeSent(false); setVerificationCode(''); sendVerificationCode(); }}
                      disabled={sendingCode}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Reenviar código
                    </button>
                  </div>
                )}
              </>
            )}

            {errors.verification && (
              <p className="mt-2 text-sm text-red-600">{errors.verification}</p>
            )}
          </div>

          {/* Botón Registrarse - Habilitado solo con email verificado */}
          <button
            type="submit"
            disabled={loading || !emailVerified}
            className={`w-full rounded-full py-3 text-white font-medium text-base transition-colors duration-200 border-0 focus:outline-none focus:ring-2 focus:ring-[rgb(0,51,153)] focus:ring-opacity-50 
                ${loading || !emailVerified ? 'bg-gray-400 cursor-not-allowed' : 'bg-[rgb(0,51,153)] hover:bg-[rgb(0,40,120)]'}`}
          >
            {loading ? 'Registrando...' : 'Registrarse'}
          </button>
        </form>
        {apiError && (
          <p className="mt-4 text-center text-sm text-red-600">{apiError}</p>
        )}

        {/* Enlace para iniciar sesión */}
        <div className="text-center mt-6">
          <p className="text-gray-600">
            ¿Ya tienes una cuenta?{' '}
            <button
              onClick={() => setCurrentPage('login')}
              className="text-[rgb(0,51,153)] font-semibold hover:text-[rgb(0,40,120)] transition-colors duration-200 focus:outline-none focus:underline"
            >
              Iniciar Sesión
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistroPage;